// ============================================================
// ENTERPRISE AI ALLUVIAL MAPPING SYSTEM - WITH CONSTRAINT CONFLICT RESOLUTION
// Complete Requirement → Approach → Layer → Sub-Layer → Component
// Enhanced with multi-approach compatibility, constraint conflicts, and resolution rules
// ============================================================

export const alluvialMapping = {
  
  // ============================================================
  // APPROACH NAMES & ICONS
  // ============================================================
  
  approachNames: {
    1: 'Build In-House',
    2: 'Buy/Subscribe SaaS',
    3: 'Partner with Vendors',
    4: 'Embed AI via APIs',
    5: 'Acquire AI Companies',
    6: 'AI Ecosystem Venture',
    7: 'Open Source Adoption',
    8: 'Hybrid Approach',
    9: 'AI-as-a-Service',
    10: 'Industry Consortium',
    11: 'AI Sovereignty',
    12: 'Edge AI',
    13: 'Federated Learning',
    14: 'Agentic AI',
    15: 'Neuro-Symbolic AI',
    16: 'Quantum-Enhanced AI',
    17: 'Human-in-the-Loop',
    18: 'AI Marketplaces',
    19: 'Regulatory Sandbox',
    20: 'Circular AI',
    21: 'AI-as-Commodity',
    22: 'Embedded AI in Hardware',
    23: 'Biologically-Inspired AI',
    24: 'Zero-Knowledge AI',
    25: 'AI Swarm Systems',
    26: 'Synthetic Data Generation',
    27: 'Causal AI',
    28: 'Multimodal AI',
    29: 'Continuous Learning',
    30: 'AI Governance & Alignment',
    31: 'Emotion AI',
    32: 'Physical AI',
    33: 'Decentralized AI',
    34: 'AI for Scientific Discovery',
    35: 'Humanoid Robotics',
    36: 'Digital Twin',
    37: 'Conversational AI',
    38: 'AI-Augmented Reality',
    39: 'Self-Healing IT Operations',
    40: 'Post-LLM Paradigms'
  },

  approachIcons: {
    1: '🏗️',
    2: '🛒',
    3: '🤝',
    4: '🔌',
    5: '🏢',
    6: '🌱',
    7: '🔓',
    8: '🔀',
    9: '☁️',
    10: '🏛️',
    11: '🏳️',
    12: '📡',
    13: '🔗',
    14: '🤖',
    15: '🧠',
    16: '⚛️',
    17: '👤',
    18: '🏪',
    19: '🧪',
    20: '♻️',
    21: '💻',
    22: '🔧',
    23: '🧬',
    24: '🔐',
    25: '🐝',
    26: '🎨',
    27: '🔍',
    28: '👁️',
    29: '🔄',
    30: '⚖️',
    31: '😊',
    32: '🦾',
    33: '🔗',
    34: '🔬',
    35: '🤖',
    36: '👥',
    37: '💬',
    38: '🥽',
    39: '🛠️',
    40: '🚀'
  },

  // ============================================================
  // APPROACH CATEGORIES
  // ============================================================
  
  approachCategories: {
    'build': [1, 8, 22, 32, 35],
    'buy': [2, 9, 18, 21],
    'partner': [3, 5, 6, 10, 33],
    'open-source': [7, 13, 15, 26, 27, 29],
    'api': [4, 37, 38, 28],
    'edge': [12, 22, 32, 35],
    'governance': [11, 19, 24, 30, 20],
    'emerging': [16, 23, 34, 40, 25],
    'autonomous': [14, 25, 39],
    'specialized': [17, 26, 31, 36]
  },

  // ============================================================
  // APPROACH COMPATIBILITY MATRIX
  // ============================================================
  
  approachCompatibility: {
    1: { compatible: [3, 7, 12, 13, 17, 22, 26, 27, 28, 29, 30, 36, 39], conflict: [2, 9, 18, 21], reason: 'Build In-House conflicts with Buy/SaaS approaches' },
    2: { compatible: [4, 9, 18, 37], conflict: [1, 8, 22], reason: 'Buy SaaS conflicts with Build approaches' },
    3: { compatible: [1, 7, 10, 30], conflict: [2, 9], reason: 'Partner approach conflicts with direct Buy' },
    4: { compatible: [2, 7, 9, 12, 18, 28, 37], conflict: [1, 22], reason: 'API approach is complementary to most' },
    5: { compatible: [1, 30], conflict: [2, 9, 18], reason: 'Acquisition conflicts with subscription models' },
    6: { compatible: [1, 7, 34], conflict: [2, 9], reason: 'Venture conflicts with direct purchase' },
    7: { compatible: [1, 3, 4, 12, 13, 15, 26, 27, 29, 36], conflict: [2, 9], reason: 'Open source conflicts with commercial SaaS' },
    8: { compatible: [1, 2, 3, 4, 7, 9, 12, 30, 37], conflict: [], reason: 'Hybrid is compatible with everything' },
    9: { compatible: [2, 4, 18, 37, 39], conflict: [1, 22, 32], reason: 'AIaaS conflicts with custom hardware builds' },
    10: { compatible: [3, 7, 30], conflict: [1, 22], reason: 'Consortium conflicts with proprietary builds' },
    11: { compatible: [24, 30, 33], conflict: [2, 9], reason: 'Sovereignty conflicts with foreign SaaS' },
    12: { compatible: [1, 4, 7, 22, 28, 29, 32, 36, 39], conflict: [2, 9], reason: 'Edge conflicts with centralized SaaS' },
    13: { compatible: [1, 7, 24, 29, 30], conflict: [2, 9], reason: 'Federated conflicts with centralized approaches' },
    14: { compatible: [1, 12, 25, 29, 39], conflict: [2, 9], reason: 'Agentic conflicts with passive SaaS' },
    15: { compatible: [1, 7, 27, 30], conflict: [2, 9], reason: 'Neuro-symbolic conflicts with black-box SaaS' },
    16: { compatible: [1, 34], conflict: [2, 9, 12], reason: 'Quantum conflicts with classical approaches' },
    17: { compatible: [1, 7, 30, 37], conflict: [], reason: 'HITL is compatible with most' },
    18: { compatible: [2, 4, 9, 37], conflict: [1, 22], reason: 'Marketplace conflicts with custom builds' },
    19: { compatible: [11, 24, 30], conflict: [2, 9], reason: 'Sandbox conflicts with unregulated approaches' },
    20: { compatible: [7, 30, 36], conflict: [22], reason: 'Circular conflicts with hardware-heavy approaches' },
    21: { compatible: [2, 4, 9, 12], conflict: [1, 22], reason: 'Commodity conflicts with premium builds' },
    22: { compatible: [1, 12, 32, 35], conflict: [2, 9, 18], reason: 'Hardware conflicts with software-only approaches' },
    23: { compatible: [1, 22], conflict: [2, 9], reason: 'Neuromorphic conflicts with standard approaches' },
    24: { compatible: [11, 13, 30, 33], conflict: [2, 9], reason: 'ZK conflicts with non-private approaches' },
    25: { compatible: [12, 14, 39], conflict: [2, 9], reason: 'Swarm conflicts with centralized approaches' },
    26: { compatible: [1, 7, 29], conflict: [], reason: 'Synthetic data is complementary' },
    27: { compatible: [1, 7, 15, 30, 36], conflict: [2, 9], reason: 'Causal conflicts with black-box approaches' },
    28: { compatible: [1, 4, 12, 37], conflict: [], reason: 'Multimodal is complementary' },
    29: { compatible: [1, 7, 12, 13, 14, 26, 36, 39], conflict: [2], reason: 'Continuous learning conflicts with static SaaS' },
    30: { compatible: [1, 3, 7, 8, 11, 17, 19, 20, 24, 27, 33, 36], conflict: [], reason: 'Governance is compatible with all' },
    31: { compatible: [4, 37], conflict: [22], reason: 'Emotion AI conflicts with hardware-only' },
    32: { compatible: [1, 12, 22, 35], conflict: [2, 9], reason: 'Physical AI conflicts with software-only' },
    33: { compatible: [11, 24, 30], conflict: [2, 9], reason: 'Decentralized conflicts with centralized SaaS' },
    34: { compatible: [1, 6, 16], conflict: [2, 9], reason: 'Scientific discovery conflicts with commercial' },
    35: { compatible: [1, 12, 22, 32], conflict: [2, 9], reason: 'Humanoid conflicts with software-only' },
    36: { compatible: [1, 7, 12, 20, 27, 29, 30], conflict: [2, 9], reason: 'Digital twin conflicts with static approaches' },
    37: { compatible: [2, 4, 9, 17, 18, 28, 31], conflict: [22], reason: 'Conversational conflicts with hardware-only' },
    38: { compatible: [12, 28, 37], conflict: [2, 9], reason: 'AR conflicts with non-interactive approaches' },
    39: { compatible: [9, 12, 14, 25, 29], conflict: [2], reason: 'Self-healing conflicts with static SaaS' },
    40: { compatible: [1, 29], conflict: [2, 9], reason: 'Post-LLM conflicts with legacy approaches' }
  },

  // ============================================================
  // CONSTRAINT CONFLICT RESOLUTION RULES
  // ============================================================
  
  constraintConflictRules: [
    {
      constraint1: 'budget-low',
      constraint2: 'quality-best',
      resolution: 'void-constraint2',
      message: 'LOW budget makes BEST quality unrealistic. Consider GOOD quality.'
    },
    {
      constraint1: 'budget-low',
      constraint2: 'scale-enterprise',
      resolution: 'void-constraint2',
      message: 'LOW budget cannot support ENTERPRISE scale. Consider SMB scale.'
    },
    {
      constraint1: 'latency-ultra-low',
      constraint2: 'budget-low',
      resolution: 'void-constraint2',
      message: 'ULTRA-LOW latency requires HIGH budget.'
    },
    {
      constraint1: 'deployment-edge',
      constraint2: 'approach-2',
      resolution: 'void-constraint2',
      message: 'EDGE deployment conflicts with SaaS approach.'
    },
    {
      constraint1: 'compliance-high',
      constraint2: 'vendor-open-source',
      resolution: 'restrict',
      message: 'HIGH compliance requires vetted solutions.'
    },
    {
      constraint1: 'expertise-beginner',
      constraint2: 'approach-1',
      resolution: 'void-constraint2',
      message: 'BEGINNER expertise cannot support Build In-House.'
    },
    {
      constraint1: 'security-critical',
      constraint2: 'approach-21',
      resolution: 'void-constraint2',
      message: 'CRITICAL security conflicts with commodity compute.'
    },
    {
      constraint1: 'time-fast',
      constraint2: 'approach-16',
      resolution: 'void-constraint2',
      message: 'FAST timeline conflicts with Quantum development.'
    },
    {
      constraint1: 'scale-startup',
      constraint2: 'approach-11',
      resolution: 'void-constraint2',
      message: 'STARTUP scale cannot support Sovereignty.'
    },
    {
      constraint1: 'priority-cost',
      constraint2: 'quality-best',
      resolution: 'void-constraint2',
      message: 'COST priority conflicts with BEST quality.'
    }
  ],

  // ============================================================
  // MULTI-APPROACH RULES
  // ============================================================
  
  multiApproachRules: {
    '1+2': {
      allowed: false,
      reason: 'Building in-house while buying SaaS is redundant',
      recommendation: 'Choose either Build OR Buy'
    },
    '1+7': {
      allowed: true,
      reason: 'Build custom on top of open source',
      recommendation: 'Use open source as base, build custom on top'
    },
    '12+9': {
      allowed: true,
      reason: 'Edge for real-time, cloud for complex',
      recommendation: 'Hybrid edge-cloud deployment'
    },
    '30+any': {
      allowed: true,
      reason: 'Governance should always be layered',
      recommendation: 'Always include governance'
    },
    '16+1': {
      allowed: true,
      reason: 'Hybrid quantum-classical',
      recommendation: 'Quantum for optimization, classical for rest'
    },
    '1+18': {
      allowed: false,
      reason: 'Building custom while using marketplace is redundant',
      recommendation: 'Choose one'
    },
    '12+2': {
      allowed: false,
      reason: 'Edge conflicts with centralized SaaS',
      recommendation: 'Edge requires custom solutions'
    },
    '13+9': {
      allowed: false,
      reason: 'Federated requires distributed, not centralized',
      recommendation: 'Federated requires custom infrastructure'
    },
    '39+2': {
      allowed: 'partial',
      reason: 'Self-healing can monitor SaaS but not control',
      recommendation: 'Use for internal systems'
    },
    '36+12': {
      allowed: true,
      reason: 'Digital twin simulates edge scenarios',
      recommendation: 'Use digital twin for planning'
    }
  },

  // ============================================================
  // REQUIREMENT APPROACH MAPPING
  // ============================================================
  
  requirementApproachMapping: {
    'business-value': {
      requirementName: 'Business Value Generation',
      description: 'Deliver measurable business value',
      priority: 'Critical',
      category: 'business',
      exactApproach: 8,
      exactApproachName: 'Hybrid Approach',
      exactApproachReason: 'Balanced approach',
      constraintMapping: {
        'budget-low': 2,
        'budget-medium': 8,
        'budget-high': 1,
        'scale-startup': 2,
        'scale-smb': 8,
        'scale-enterprise': 1,
        'expertise-beginner': 2,
        'expertise-intermediate': 8,
        'expertise-expert': 1,
        'time-fast': 2,
        'time-balanced': 8,
        'time-extended': 1,
        'quality-good': 2,
        'quality-better': 8,
        'quality-best': 1
      },
      approaches: {
        1: { name: 'Build In-House', layers: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'] },
        2: { name: 'Buy/Subscribe SaaS', layers: ['layer10','layer11'] },
        8: { name: 'Hybrid Approach', layers: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'] }
      }
    },
    'data-processing': {
      requirementName: 'Data Processing Capability',
      description: 'Process data effectively',
      priority: 'High',
      category: 'functional',
      exactApproach: 7,
      exactApproachName: 'Open Source Adoption',
      exactApproachReason: 'Open source tools',
      constraintMapping: {
        'budget-low': 7,
        'budget-medium': 7,
        'budget-high': 1,
        'expertise-beginner': 9,
        'expertise-intermediate': 7,
        'expertise-expert': 1,
        'latency-batch': 7,
        'latency-standard': 7,
        'latency-real-time': 1,
        'latency-ultra-low': 22
      },
      approaches: {
        1: { name: 'Build In-House', layers: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'] },
        7: { name: 'Open Source Adoption', layers: ['layer1','layer3','layer4','layer5','layer6','layer8'] },
        9: { name: 'AI-as-a-Service', layers: ['layer1','layer3','layer4','layer6','layer8','layer10','layer11'] },
        22: { name: 'Embedded AI in Hardware', layers: ['layer1','layer6','layer8'] }
      }
    },
    'model-inference': {
      requirementName: 'Model Inference',
      description: 'Perform inference efficiently',
      priority: 'High',
      category: 'functional',
      exactApproach: 12,
      exactApproachName: 'Edge AI',
      exactApproachReason: 'Edge for inference',
      constraintMapping: {
        'latency-batch': 21,
        'latency-standard': 9,
        'latency-real-time': 12,
        'latency-ultra-low': 22,
        'deployment-cloud': 9,
        'deployment-on-premise': 1,
        'deployment-edge': 12,
        'budget-low': 21,
        'budget-medium': 9,
        'budget-high': 12
      },
      approaches: {
        1: { name: 'Build In-House', layers: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'] },
        9: { name: 'AI-as-a-Service', layers: ['layer1','layer3','layer4','layer6','layer8','layer10','layer11'] },
        12: { name: 'Edge AI', layers: ['layer1','layer2','layer3','layer5','layer6','layer8','layer10'] },
        21: { name: 'AI-as-Commodity', layers: ['layer1','layer6','layer8'] },
        22: { name: 'Embedded AI in Hardware', layers: ['layer1','layer6','layer8'] }
      }
    },
    'scalable-infrastructure': {
      requirementName: 'Scalable Infrastructure',
      description: 'Scale with demand',
      priority: 'High',
      category: 'technical',
      exactApproach: 1,
      exactApproachName: 'Build In-House',
      exactApproachReason: 'Custom infrastructure',
      constraintMapping: {
        'scale-startup': 9,
        'scale-smb': 7,
        'scale-enterprise': 1,
        'deployment-cloud': 9,
        'deployment-on-premise': 1,
        'deployment-edge': 12,
        'budget-low': 9,
        'budget-medium': 7,
        'budget-high': 1
      },
      approaches: {
        1: { name: 'Build In-House', layers: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'] },
        7: { name: 'Open Source Adoption', layers: ['layer1','layer3','layer4','layer5','layer6','layer8'] },
        9: { name: 'AI-as-a-Service', layers: ['layer1','layer3','layer4','layer6','layer8','layer10','layer11'] },
        12: { name: 'Edge AI', layers: ['layer1','layer2','layer3','layer5','layer6','layer8','layer10'] }
      }
    },
    'data-storage': {
      requirementName: 'Data Storage',
      description: 'Store data efficiently',
      priority: 'High',
      category: 'data',
      exactApproach: 7,
      exactApproachName: 'Open Source Adoption',
      exactApproachReason: 'Open source databases',
      constraintMapping: {
        'budget-low': 7,
        'budget-medium': 7,
        'budget-high': 1,
        'security-standard': 7,
        'security-enhanced': 24,
        'security-critical': 11,
        'compliance-low': 7,
        'compliance-medium': 7,
        'compliance-high': 1
      },
      approaches: {
        1: { name: 'Build In-House', layers: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'] },
        7: { name: 'Open Source Adoption', layers: ['layer1','layer3','layer4','layer5','layer6','layer8'] },
        11: { name: 'AI Sovereignty', layers: ['layer1','layer3','layer4','layer11','layer12'] },
        24: { name: 'Zero-Knowledge AI', layers: ['layer3','layer4','layer6','layer8','layer11','layer12'] }
      }
    },
    'data-protection': {
      requirementName: 'Data Protection',
      description: 'Protect data',
      priority: 'Critical',
      category: 'security',
      exactApproach: 30,
      exactApproachName: 'AI Governance',
      exactApproachReason: 'Governance for security',
      constraintMapping: {
        'security-standard': 30,
        'security-enhanced': 24,
        'security-critical': 11,
        'compliance-low': 30,
        'compliance-medium': 19,
        'compliance-high': 30
      },
      approaches: {
        11: { name: 'AI Sovereignty', layers: ['layer1','layer3','layer4','layer11','layer12'] },
        19: { name: 'Regulatory Sandbox', layers: ['layer3','layer4','layer11','layer12'] },
        24: { name: 'Zero-Knowledge AI', layers: ['layer3','layer4','layer6','layer8','layer11','layer12'] },
        30: { name: 'AI Governance & Alignment', layers: ['layer11','layer12'] }
      }
    },
    'real-time-processing': {
      requirementName: 'Real-time Processing',
      description: 'Process in real-time',
      priority: 'High',
      category: 'performance',
      exactApproach: 12,
      exactApproachName: 'Edge AI',
      exactApproachReason: 'Edge for real-time',
      constraintMapping: {
        'latency-batch': 21,
        'latency-standard': 21,
        'latency-real-time': 12,
        'latency-ultra-low': 22,
        'budget-low': 21,
        'budget-medium': 12,
        'budget-high': 22
      },
      approaches: {
        12: { name: 'Edge AI', layers: ['layer1','layer2','layer3','layer5','layer6','layer8','layer10'] },
        21: { name: 'AI-as-Commodity', layers: ['layer1','layer6','layer8'] },
        22: { name: 'Embedded AI in Hardware', layers: ['layer1','layer6','layer8'] }
      }
    },
    'regulatory-compliance': {
      requirementName: 'Regulatory Compliance',
      description: 'Comply with regulations',
      priority: 'Critical',
      category: 'compliance',
      exactApproach: 30,
      exactApproachName: 'AI Governance',
      exactApproachReason: 'Governance for compliance',
      constraintMapping: {
        'compliance-low': 30,
        'compliance-medium': 19,
        'compliance-high': 30
      },
      approaches: {
        11: { name: 'AI Sovereignty', layers: ['layer1','layer3','layer4','layer11','layer12'] },
        19: { name: 'Regulatory Sandbox', layers: ['layer3','layer4','layer11','layer12'] },
        30: { name: 'AI Governance & Alignment', layers: ['layer11','layer12'] }
      }
    },
    'monitoring-observability': {
      requirementName: 'Monitoring',
      description: 'System monitorable',
      priority: 'High',
      category: 'operational',
      exactApproach: 39,
      exactApproachName: 'Self-Healing Ops',
      exactApproachReason: 'Self-healing for monitoring',
      constraintMapping: {
        'scale-startup': 9,
        'scale-smb': 39,
        'scale-enterprise': 39,
        'budget-low': 9,
        'budget-medium': 39,
        'budget-high': 39
      },
      approaches: {
        9: { name: 'AI-as-a-Service', layers: ['layer1','layer3','layer4','layer6','layer8','layer10','layer11'] },
        12: { name: 'Edge AI', layers: ['layer1','layer2','layer3','layer5','layer6','layer8','layer10'] },
        39: { name: 'Self-Healing IT Operations', layers: ['layer8','layer9','layer11','layer12'] }
      }
    }
  },

  // ============================================================
  // APPROACH → LAYER MAPPING
  // ============================================================
  
  approachLayerMapping: {
    1: { layers: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'] },
    2: { layers: ['layer10','layer11'] },
    3: { layers: ['layer1','layer3','layer4','layer6','layer8','layer10'] },
    4: { layers: ['layer3','layer4','layer5','layer6','layer8','layer10'] },
    5: { layers: ['layer1','layer6','layer8'] },
    6: { layers: ['layer1','layer6'] },
    7: { layers: ['layer1','layer3','layer4','layer5','layer6','layer8'] },
    8: { layers: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'] },
    9: { layers: ['layer1','layer3','layer4','layer6','layer8','layer10','layer11'] },
    10: { layers: ['layer1','layer3','layer4','layer6'] },
    11: { layers: ['layer1','layer3','layer4','layer11','layer12'] },
    12: { layers: ['layer1','layer2','layer3','layer5','layer6','layer8','layer10'] },
    13: { layers: ['layer1','layer3','layer4','layer5','layer6','layer8','layer11'] },
    14: { layers: ['layer5','layer6','layer7','layer8','layer9','layer10','layer11'] },
    15: { layers: ['layer5','layer6','layer7','layer11'] },
    16: { layers: ['layer1','layer6','layer7'] },
    17: { layers: ['layer5','layer6','layer8','layer10','layer11','layer12'] },
    18: { layers: ['layer3','layer4','layer6','layer8','layer10'] },
    19: { layers: ['layer3','layer4','layer11','layer12'] },
    20: { layers: ['layer1','layer3','layer4','layer11','layer12'] },
    21: { layers: ['layer1','layer6','layer8'] },
    22: { layers: ['layer1','layer6','layer8'] },
    23: { layers: ['layer1','layer6','layer8'] },
    24: { layers: ['layer3','layer4','layer6','layer8','layer11','layer12'] },
    25: { layers: ['layer6','layer8','layer9','layer11'] },
    26: { layers: ['layer3','layer4','layer5','layer11'] },
    27: { layers: ['layer5','layer6','layer7','layer11'] },
    28: { layers: ['layer2','layer3','layer5','layer6','layer8','layer10'] },
    29: { layers: ['layer3','layer4','layer5','layer6','layer8','layer11'] },
    30: { layers: ['layer11','layer12'] },
    31: { layers: ['layer2','layer5','layer6','layer10'] },
    32: { layers: ['layer1','layer2','layer6','layer8','layer10'] },
    33: { layers: ['layer1','layer3','layer4','layer11','layer12'] },
    34: { layers: ['layer3','layer4','layer5','layer6','layer7'] },
    35: { layers: ['layer1','layer2','layer6','layer8','layer10'] },
    36: { layers: ['layer1','layer3','layer4','layer5','layer6','layer8','layer11'] },
    37: { layers: ['layer2','layer5','layer6','layer8','layer10','layer11'] },
    38: { layers: ['layer2','layer5','layer6','layer10'] },
    39: { layers: ['layer8','layer9','layer11','layer12'] },
    40: { layers: ['layer1','layer5','layer6','layer7','layer8'] }
  },

  // ============================================================
  // LAYER STATISTICS
  // ============================================================
  
  layerStatistics: {
    "layer1": { name: "Physical Substrate & Compute", componentCount: 15 },
    "layer2": { name: "Environmental Interface", componentCount: 8 },
    "layer3": { name: "Data Acquisition & Generation", componentCount: 12 },
    "layer4": { name: "Data Structuring & Storage", componentCount: 10 },
    "layer5": { name: "Data Processing & Feature Engineering", componentCount: 14 },
    "layer6": { name: "Model Architecture & Learning", componentCount: 20 },
    "layer7": { name: "Objective & Optimization", componentCount: 6 },
    "layer8": { name: "Compilation & Inference Engine", componentCount: 12 },
    "layer9": { name: "Orchestration & Integration", componentCount: 4 },
    "layer10": { name: "Application & User Interface", componentCount: 10 },
    "layer11": { name: "Observability & Security", componentCount: 14 },
    "layer12": { name: "Governance & Policy", componentCount: 8 }
  }
};

// ============================================================
// EXPORTED FUNCTIONS
// ============================================================

export function getExactApproach(requirementCategory, constraints) {
  const reqData = alluvialMapping.requirementApproachMapping[requirementCategory];
  if (!reqData) return null;
  
  const constraintKeys = [];
  const c = constraints || {};
  
  if (c.priority) constraintKeys.push('priority-' + c.priority);
  if (c.budget) constraintKeys.push('budget-' + c.budget);
  if (c.time) constraintKeys.push('time-' + c.time);
  if (c.quality) constraintKeys.push('quality-' + c.quality);
  if (c.scale) constraintKeys.push('scale-' + c.scale);
  if (c.latency) constraintKeys.push('latency-' + c.latency);
  if (c.deployment) constraintKeys.push('deployment-' + c.deployment);
  if (c.compliance) constraintKeys.push('compliance-' + c.compliance);
  if (c.security) constraintKeys.push('security-' + c.security);
  if (c.expertise) constraintKeys.push('expertise-' + c.expertise);
  
  for (var i = 0; i < constraintKeys.length; i++) {
    var key = constraintKeys[i];
    if (reqData.constraintMapping && reqData.constraintMapping[key]) {
      var approachId = reqData.constraintMapping[key];
      return {
        approachId: approachId,
        approachName: alluvialMapping.approachNames[approachId] || reqData.approaches[approachId]?.name || 'Approach ' + approachId,
        reason: 'Selected based on: ' + key,
        matchedConstraint: key
      };
    }
  }
  
  return {
    approachId: reqData.exactApproach,
    approachName: reqData.exactApproachName,
    reason: reqData.exactApproachReason,
    matchedConstraint: 'default'
  };
}

export function getApproachLayers(approachId) {
  var mapping = alluvialMapping.approachLayerMapping[approachId];
  return mapping ? mapping.layers : [];
}

export function getLayerName(layerId) {
  var layer = alluvialMapping.layerStatistics[layerId];
  return layer ? layer.name : layerId;
}

export function getAllRequirementCategories() {
  return Object.keys(alluvialMapping.requirementApproachMapping).map(function(key) {
    var req = alluvialMapping.requirementApproachMapping[key];
    return {
      id: key,
      name: req.requirementName,
      description: req.description,
      priority: req.priority,
      category: req.category,
      exactApproach: req.exactApproach,
      exactApproachName: req.exactApproachName
    };
  });
}

export function checkApproachCompatibility(approachId1, approachId2) {
  var compat = alluvialMapping.approachCompatibility[approachId1];
  if (!compat) return { allowed: true, reason: 'No data' };
  if (compat.compatible && compat.compatible.includes(approachId2)) {
    return { allowed: true, reason: 'Compatible' };
  }
  if (compat.conflict && compat.conflict.includes(approachId2)) {
    return { allowed: false, reason: compat.reason };
  }
  return { allowed: true, reason: 'No known conflict' };
}

export function resolveConstraintConflicts(constraints) {
  var resolved = Object.assign({}, constraints || {});
  var resolutions = [];
  
  alluvialMapping.constraintConflictRules.forEach(function(rule) {
    var c1 = rule.constraint1.split('-');
    var c2 = rule.constraint2.split('-');
    
    if (constraints && constraints[c1[0]] === c1[1] && constraints[c2[0]] === c2[1]) {
      if (rule.resolution === 'void-constraint2') {
        delete resolved[c2[0]];
        resolutions.push(rule.message);
      }
    }
  });
  
  return { resolved: resolved, resolutions: resolutions };
}

export function getCompatibleApproaches(selectedApproachIds) {
  var allIds = Object.keys(alluvialMapping.approachNames).map(Number);
  var compatible = [];
  
  allIds.forEach(function(approachId) {
    var isCompatible = true;
    for (var i = 0; i < selectedApproachIds.length; i++) {
      var check = checkApproachCompatibility(selectedApproachIds[i], approachId);
      if (!check.allowed) {
        isCompatible = false;
        break;
      }
    }
    if (isCompatible && selectedApproachIds.indexOf(approachId) === -1) {
      compatible.push({
        approachId: approachId,
        approachName: alluvialMapping.approachNames[approachId],
        approachIcon: alluvialMapping.approachIcons[approachId]
      });
    }
  });
  
  return compatible;
}

export function generateAlluvialFlow(constraints) {
  var flows = [];
  
  Object.keys(alluvialMapping.requirementApproachMapping).forEach(function(reqCategory) {
    var reqData = alluvialMapping.requirementApproachMapping[reqCategory];
    var exactApproach = getExactApproach(reqCategory, constraints);
    
    if (exactApproach) {
      var layers = getApproachLayers(exactApproach.approachId);
      layers.forEach(function(layerId) {
        flows.push({
          requirementCategory: reqCategory,
          requirementName: reqData.requirementName,
          approachId: exactApproach.approachId,
          approachName: exactApproach.approachName,
          layerId: layerId,
          layerName: getLayerName(layerId)
        });
      });
    }
  });
  
  return flows;
}

export default alluvialMapping;

// ============================================================
// ENHANCED FUNCTIONS WITH CONFLICT RESOLUTION
// ============================================================

function getConstraintKey(constraintString) {
  // 'budget-low' → { budget: 'low' }
  // 'approach-2' → { approach: 2 }
  const parts = constraintString.split('-');
  if (parts[0] === 'approach') {
    return 'selectedApproach';
  }
  return parts[0];
}

export function getMultiApproachRule(approachId1, approachId2) {
  const key1 = `${approachId1}+${approachId2}`;
  const key2 = `${approachId2}+${approachId1}`;
  
  return alluvialMapping.multiApproachRules[key1] || alluvialMapping.multiApproachRules[key2] || null;
}

